import { ApiError } from '@/api/client'
import { fetchRecipeExport, useFieldOptions, useRecipes } from '@/api/hooks'
import { buttonClasses } from '@/components/ui/buttonStyles'
import {
  EffortBadge,
  RatingStars,
  VegetarianBadge,
} from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { CuisineDot } from '@/components/ui/CuisineAccent'
import { EmptyState, ErrorNote, SkeletonRows } from '@/components/ui/Feedback'
import { Menu } from '@/components/ui/Menu'
import { Pagination } from '@/components/ui/Pagination'
import { useToast } from '@/components/ui/toastContext'
import { downloadJson } from '@/lib/download'
import { PageHeader, PageShell } from '@/components/layout/Page'
import { RecipeCard } from './RecipeCard'
import { RecipePreviewDrawer } from './RecipePreview'
import { PreviewButton } from '@/components/ui/PreviewButton'
import { Pencil } from 'lucide-react'
import { FilterBar, type FilterState } from './FilterBar'
import { SORT_OPTIONS, buildSearchParams, parseSearchParams } from './filters'
import { formatKcal, formatMacroGrams, relativeFromNow } from '@/lib/format'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { ArrowDown, ArrowUp, ChefHat, Download, FileInput, Plus } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

const PAGE_SIZE = 24

export function RecipeListPage() {
  useDocumentTitle('Recipes')
  const [params, setParams] = useSearchParams()
  const parsed = parseSearchParams(params)
  const fieldOptions = useFieldOptions()
  const [offset, setOffset] = useState(0)
  const [previewId, setPreviewId] = useState<number | null>(null)
  const toast = useToast()

  async function exportAll() {
    try {
      downloadJson('recipes.json', await fetchRecipeExport())
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not export.')
    }
  }

  const query = useRecipes(
    {
      ...parsed.filters,
      sort: parsed.sort,
      order: parsed.order,
    },
    { limit: PAGE_SIZE, offset },
  )

  function update(changes: Partial<FilterState>) {
    const next = buildSearchParams({
      q: changes.q ?? parsed.q,
      cuisine: changes.cuisine ?? parsed.filters.cuisine ?? [],
      protein_base: changes.protein_base ?? parsed.filters.protein_base ?? [],
      tags: changes.tags ?? parsed.filters.tags ?? [],
      vegetarian: changes.vegetarian ?? parsed.vegetarian,
      excluded: changes.excluded ?? parsed.excluded,
      min_rating: changes.min_rating ?? parsed.filters.min_rating ?? null,
      max_effort: changes.max_effort ?? parsed.filters.max_effort ?? null,
      not_prepped_within_days:
        changes.not_prepped_within_days ?? parsed.filters.not_prepped_within_days ?? null,
      sort: changes.sort ?? parsed.sort,
      order: changes.order ?? parsed.order,
      view: changes.view ?? parsed.view,
    })
    setParams(next, { replace: true })
    setOffset(0)
  }

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const state: FilterState = {
    q: parsed.q,
    cuisine: parsed.filters.cuisine ?? [],
    protein_base: parsed.filters.protein_base ?? [],
    tags: parsed.filters.tags ?? [],
    vegetarian: parsed.vegetarian,
    excluded: parsed.excluded,
    min_rating: parsed.filters.min_rating ?? null,
    max_effort: parsed.filters.max_effort ?? null,
    not_prepped_within_days: parsed.filters.not_prepped_within_days ?? null,
    sort: parsed.sort,
    order: parsed.order,
    view: parsed.view,
  }

  if (query.isError) {
    const message =
      query.error instanceof ApiError ? query.error.message : 'Could not load recipes.'
    return (
      <PageShell>
        <ErrorNote body={message} onRetry={() => void query.refetch()} />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHeader
        title="Recipes"
        subtitle={total > 0 ? `${total} in the collection` : 'Nothing here yet'}
        actions={
          <>
            <Menu
              items={[
                {
                  label: 'Export all recipes',
                  icon: <Download size={16} />,
                  onSelect: () => void exportAll(),
                },
              ]}
            />
            <Link to="/recipes/import" className={buttonClasses('secondary')}>
              <FileInput size={16} aria-hidden />
              Import
            </Link>
            <Link to="/recipes/new" className={buttonClasses('primary')}>
              <Plus size={16} aria-hidden />
              New recipe
            </Link>
          </>
        }
      />

      <div className="space-y-5">
        <FilterBar
          state={state}
          options={fieldOptions.data}
          total={total}
          onChange={update}
          sortOptions={SORT_OPTIONS}
        />

        {query.isPending ? (
          <SkeletonRows rows={6} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ChefHat size={22} />}
            title="No recipes match"
            body="Adjust the filters, or add a recipe to get started."
            action={
              <Link to="/recipes/new" className={buttonClasses('primary')}>
                <Plus size={15} aria-hidden />
                New recipe
              </Link>
            }
          />
        ) : parsed.view === 'grid' ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {items.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onPreview={setPreviewId}
                />
              ))}
            </div>
            <Pagination total={total} offset={offset} pageSize={PAGE_SIZE} onChange={setOffset} />
          </>
        ) : (
          <>
            <RecipeTable
              items={items}
              sort={parsed.sort}
              order={parsed.order}
              onSort={(column) =>
                update(
                  parsed.sort === column
                    ? { order: parsed.order === 'asc' ? 'desc' : 'asc' }
                    : { sort: column, order: 'asc' },
                )
              }
              onPreview={setPreviewId}
            />
            <Pagination total={total} offset={offset} pageSize={PAGE_SIZE} onChange={setOffset} />
          </>
        )}
      </div>

      <RecipePreviewDrawer recipeId={previewId} onClose={() => setPreviewId(null)} />
    </PageShell>
  )
}

function RecipeTable({
  items,
  sort,
  order,
  onSort,
  onPreview,
}: {
  items: Array<import('@/api/types').RecipeSummary>
  sort: string
  order: 'asc' | 'desc'
  onSort: (column: string) => void
  onPreview: (id: number) => void
}) {
  const columns: Array<{ key: string; label: string; sortable: boolean; numeric?: boolean }> = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'cuisine', label: 'Cuisine', sortable: true },
    { key: 'protein_base', label: 'Protein', sortable: true },
    { key: 'tags', label: 'Tags', sortable: false },
    { key: 'vegetarian', label: 'Veg', sortable: false },
    { key: 'rating', label: 'Rating', sortable: true },
    { key: 'effort', label: 'Effort', sortable: true },
    { key: 'prep_count', label: 'Made', sortable: true, numeric: true },
    { key: 'last_prep', label: 'Last made', sortable: true },
    { key: 'calories', label: 'kcal', sortable: true, numeric: true },
    { key: 'protein_per_serving', label: 'Protein', sortable: true, numeric: true },
    { key: 'carbs_per_serving', label: 'Carbs', sortable: true, numeric: true },
    { key: 'fat_per_serving', label: 'Fat', sortable: true, numeric: true },
    { key: 'fiber_per_serving', label: 'Fibre', sortable: true, numeric: true },
    { key: 'ingredient_count', label: 'Ingredients', sortable: true, numeric: true },
  ]

  return (
    <Card className="overflow-hidden">
      <div className="scroll-slim overflow-x-auto">
        <table className="w-full min-w-[80rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line bg-surface-sunken">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={column.numeric ? 'px-3 py-2 text-right' : 'px-3 py-2 text-left'}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.key)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-ink-faint hover:text-ink"
                    >
                      {column.label}
                      {sort === column.key ? (
                        order === 'asc' ? (
                          <ArrowUp size={12} aria-hidden />
                        ) : (
                          <ArrowDown size={12} aria-hidden />
                        )
                      ) : null}
                    </button>
                  ) : (
                    <span className="text-xs font-medium text-ink-faint">
                      {column.label}
                    </span>
                  )}
                </th>
              ))}
              <th scope="col" className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((recipe) => (
              <tr
                key={recipe.id}
                className="border-b border-line last:border-0 hover:bg-surface-sunken/70"
              >
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <CuisineDot cuisine={recipe.cuisine} />
                    <Link
                      to={`/recipes/${recipe.id}`}
                      className="font-medium text-ink hover:text-accent"
                    >
                      {recipe.name}
                    </Link>
                    {recipe.excluded ? (
                      <span className="text-micro text-critical">excluded</span>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-2 text-ink-soft">{recipe.cuisine ?? '—'}</td>
                <td className="px-3 py-2 text-ink-soft">{recipe.protein_base ?? '—'}</td>
                <td className="px-3 py-2 text-ink-soft">{recipe.tags.join(', ') || '—'}</td>
                <td className="px-3 py-2">
                  <VegetarianBadge vegetarian={recipe.vegetarian} />
                </td>
                <td className="px-3 py-2">
                  <RatingStars value={recipe.rating} showValue />
                </td>
                <td className="px-3 py-2">
                  <EffortBadge level={recipe.effort_level} />
                </td>
                <td className="tabular px-3 py-2 text-right text-ink-soft">{recipe.prep_count}×</td>
                <td
                  className="px-3 py-2 text-ink-soft"
                  title={recipe.last_prep ?? 'Never prepared'}
                >
                  {relativeFromNow(recipe.last_prep)}
                </td>
                <td className="tabular px-3 py-2 text-right text-ink-soft">
                  {formatKcal(recipe.kcal_per_serving)}
                </td>
                <td className="tabular px-3 py-2 text-right text-ink-soft">
                  {formatMacroGrams(recipe.protein_per_serving)}
                </td>
                <td className="tabular px-3 py-2 text-right text-ink-soft">
                  {formatMacroGrams(recipe.carbs_per_serving)}
                </td>
                <td className="tabular px-3 py-2 text-right text-ink-soft">
                  {formatMacroGrams(recipe.fat_per_serving)}
                </td>
                <td className="tabular px-3 py-2 text-right text-ink-soft">
                  {formatMacroGrams(recipe.fiber_per_serving)}
                </td>
                <td className="tabular px-3 py-2 text-right text-ink-soft">
                  {recipe.ingredient_count}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <PreviewButton iconOnly onClick={() => onPreview(recipe.id)} />
                    <Link
                      to={`/recipes/${recipe.id}/edit`}
                      aria-label="Edit"
                      title="Edit"
                      className={buttonClasses('ghost', 'icon-sm', 'text-ink-faint')}
                    >
                      <Pencil size={14} aria-hidden />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
